/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/pages/Documents.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Search, Download, Upload, ExternalLink, FileText, FileSearch, SearchX, FilterX, Inbox, BookOpen, ListChecks, QrCode, ShieldCheck, TriangleAlert, ScanText, Tags, Wallet, Info, Lock, Check, FileCheck2, FilePlus2, X } from 'lucide-react';
import { useApi, useAutofocus, isTyping, modalOpen } from '../lib/hooks';
import { navigate } from '../lib/router';
import { I, Badge, Confidence, Drawer, Empty, ErrorCard, LoadingRows, Skeleton, Spinner } from '../components/ui';
import { Combobox, type Option } from '../components/Combobox';
import { useAccounts } from '../lib/accounts';
import { RevisionExportButton } from '../components/RevisionExport';
import { amt, chf, date, dateTime } from '../lib/format';
import { LineItems } from '../components/LineItems';
import '../styles/documents.css';
import { PromptButton } from '../components/PromptView';
import { api } from '../lib/api';

export interface ReceiptSealBadge {
  id: string;
  sealKind: 'receipt' | 'missing_receipt_declaration' | 'eigenbeleg' | 'invoice_in' | 'document';
  sealStandard: string;
  sealProvider: string;
  originalMime: string;
  originalSha256: string;
  convertedToPdf: boolean;
  sealedPdfSha256: string;
  sealedPdfSize: number;
  sealedPdfFilename: string;
  certSubject: string;
  certFingerprintSha256: string;
  tsaPolicyOid: string;
  tsaSerialHex: string;
  tsaStampedAt: string;
  probativeValueDe: string;
  probativeLawRefs: string[];
  sealedBy: string;
  sealedAt: string;
}

export interface SealSummary {
  totalDocuments: number;
  totalSealed: number;
  unsealedCount: number;
  convertedFromImageCount: number;
  missingReceiptDeclarationCount: number;
  sealStandard: string;
  sealProvider: string;
  certSubject: string;
  certFingerprintSha256: string;
}

/** One search hit, as returned by GET /api/t/:tid/documents/search (amounts in Rappen). */
export interface DocHit {
  id: string; filename: string; mime: string; size: number; sha256: string; channel: string; kind: string | null; status: string; summary: string | null; created_at: string;
  vendor: string | null; docDate: string | null; documentNumber: string | null; reference: string | null;
  currency: string | null; total: number | null; totalChf: number | null; vatTotal: number | null;
  fields: Record<string, unknown> | null;
  entry: { id: number; seq: number; date: string; reversed: boolean } | null;
  proposal: { id: string; status: string } | null;
  rank: number | null; snippet: string | null;
  seal?: ReceiptSealBadge | null;
}
interface SearchResult { q: string; engine: 'fulltext'; total: number; items: DocHit[] }

/** Navigation icon for the page (exported so App.tsx needs a single import line). */
export const DocumentsIcon = FileSearch;

export const DOC_KIND: Record<string, string> = {
  receipt: 'Quittung', invoice_in: 'Kreditorenrechnung', invoice_out: 'Debitorenrechnung', bank_statement: 'Bankauszug', payroll: 'Lohndokument', contract: 'Vertrag', other: 'Dokument',
  credit_note: 'Gutschrift', dunning: 'Mahnung', jahresrechnung: 'Jahresrechnung', payslip: 'Lohnabrechnung', lohnausweis: 'Lohnausweis', vat_return: 'MWST-Abrechnung', report: 'Bericht', tax_return: 'Steuererklärung', eigenbeleg: 'Eigenbeleg', dossier: 'Unterlage (FINMA-Dossier)',
  vat_confirmation: 'ESTV-Bestätigung',
  import: 'Importdatei',
};
const STATUS: Record<string, [string, 'accent' | 'pos' | 'warn' | 'neg' | undefined]> = {
  received: ['Eingegangen', undefined], processing: ['In Arbeit', 'accent'], proposed: ['Zur Prüfung', 'warn'], booked: ['Gebucht', 'pos'], needs_input: ['Rückfrage', 'warn'], filed: ['Abgelegt', undefined], error: ['Fehler', 'neg'],
  // A bank file Kontor refused (ADR-059): no Beleg, listed only with this filter.
  refused: ['Abgewiesen', 'neg'],
  purged: ['Gelöscht (Frist abgelaufen)', undefined],
};
const CHANNEL: Record<string, string> = { upload: 'Upload', email: 'E-Mail', chat: 'Chat', api: 'API', import: 'Import', generated: 'Von Kontor erstellt', link: 'Upload-Link', threema: 'Threema', whatsapp: 'WhatsApp' };
const PROPOSAL: Record<string, string> = { pending: 'Vorschlag wartet auf Prüfung', approved: 'Vorschlag freigegeben', rejected: 'Vorschlag abgelehnt', auto_posted: 'Automatisch verbucht' };
const PAY: Record<string, string> = { card: 'Karte', cash: 'Bar', bank: 'Überweisung', twint: 'TWINT', invoice: 'Auf Rechnung' };
const KIND_OPTS: Option[] = [{ value: '', label: 'Alle Arten' }, ...Object.entries(DOC_KIND).map(([value, label]) => ({ value, label }))];
const STATUS_OPTS: Option[] = [{ value: '', label: 'Alle Status' }, ...Object.entries(STATUS).map(([value, [label]]) => ({ value, label }))];
const SORT_OPTS: Option[] = [
  { value: '', label: 'Sortierung: Eingang / Relevanz' },
  { value: 'date_desc', label: 'Belegdatum ↓ (neueste zuerst)' },
  { value: 'date_asc', label: 'Belegdatum ↑ (älteste zuerst)' },
  { value: 'amount_desc', label: 'Betrag CHF ↓ (höchste zuerst)' },
  { value: 'amount_asc', label: 'Betrag CHF ↑ (kleinste zuerst)' },
];

const localDate = (iso: string) => new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
const localTime = (iso: string) => new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
const money = (r: number | null | undefined, cur?: string | null) => (r == null ? '—' : `${cur ?? 'CHF'} ${chf(r, { currency: false })}`);
const iban = (s: string) => s.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();

/** ⟦hit⟧ markers from the API become <mark> elements; the text itself is never parsed as HTML. */
export function Snippet({ text }: { text: string }) {
  return <>{text.split(/⟦|⟧/).map((p, i) => (i % 2 ? <mark key={i} className="hit">{p}</mark> : p))}</>;
}

export function StatusPill({ status }: { status: string }) {
  const [label, kind] = STATUS[status] ?? [status, undefined];
  return <Badge kind={kind} dot={status === 'processing'}>{label}</Badge>;
}

export function Documents({ tid, query }: { tid: string; query: URLSearchParams }) {
  const [q, setQ] = useState(query.get('q') ?? '');
  const [debounced, setDebounced] = useState(q.trim());
  const [kind, setKind] = useState(query.get('kind') ?? '');
  const [status, setStatus] = useState(query.get('status') ?? '');
  const [vendor, setVendor] = useState(query.get('vendor') ?? '');
  const [minChf, setMinChf] = useState(query.get('minChf') ?? '');
  const [maxChf, setMaxChf] = useState(query.get('maxChf') ?? '');
  const [sort, setSort] = useState(query.get('sort') ?? '');
  const [offset, setOffset] = useState(Number(query.get('offset') ?? 0) || 0);
  const [sel, setSel] = useState(0);
  const [open, setOpen] = useState<DocHit | null>(null);
  const [linked, setLinked] = useState(query.get('d') ?? ''); // /t/<tid>/documents?d=<id> opens one Beleg (BF-04)
  const [showQesExplainer, setShowQesExplainer] = useState(false);
  const [showMissingDeclModal, setShowMissingDeclModal] = useState(false);
  const [inspectSealDoc, setInspectSealDoc] = useState<DocHit | null>(null);
  const [sealingAll, setSealingAll] = useState(false);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [uploadingQes, setUploadingQes] = useState(false);
  const [qesBannerMsg, setQesBannerMsg] = useState<{ kind: 'pos' | 'neg'; text: string } | null>(null);
  const input = useAutofocus<HTMLInputElement>();
  const body = useRef<HTMLTableSectionElement>(null);
  const qesFileInput = useRef<HTMLInputElement>(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setOffset(0); }, [debounced, kind, status, vendor, minChf, maxChf, sort]);
  const minAmount = minChf.trim() && Number.isFinite(Number(minChf)) ? String(Math.round(Number(minChf) * 100)) : '';
  const maxAmount = maxChf.trim() && Number.isFinite(Number(maxChf)) ? String(Math.round(Number(maxChf) * 100)) : '';
  const params = useMemo(() => new URLSearchParams(Object.entries({ q: debounced, kind, status, vendor: vendor.trim(), minAmount, maxAmount, sort, offset: offset > 0 ? String(offset) : '' }).filter(([, v]) => v)).toString(), [debounced, kind, status, vendor, minAmount, maxAmount, sort, offset]);
  const shown = open?.id ?? linked;
  useEffect(() => { const s = [params, shown ? `d=${encodeURIComponent(shown)}` : ''].filter(Boolean).join('&'); navigate(`/t/${tid}/documents${s ? `?${s}` : ''}`, true); }, [tid, params, shown]); // searches and single Belege are linkable
  const { data, error, loading, reload } = useApi<SearchResult>(`/api/t/${tid}/documents/search?limit=200${params ? `&${params}` : ''}`);
  const sealSummary = useApi<SealSummary>(`/api/t/${tid}/documents/seals/summary`);
  const one = useApi<SearchResult>(linked ? `/api/t/${tid}/documents/search?id=${encodeURIComponent(linked)}` : null);
  useEffect(() => { const hit = one.data?.items[0]; if (hit) setOpen(hit); }, [one.data]);
  const close = () => { setOpen(null); setLinked(''); };
  const items = data?.items ?? [];
  useEffect(() => { setSel(0); }, [data]);
  useEffect(() => { body.current?.querySelector('tr[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [sel]);

  const move = (d: number) => setSel((s) => Math.max(0, Math.min(items.length - 1, s + d)));
  const reset = () => { setQ(''); setDebounced(''); setKind(''); setStatus(''); setVendor(''); setMinChf(''); setMaxChf(''); setSort(''); setOffset(0); input.current?.focus(); };
  const upload = () => navigate(`/t/${tid}/agent?upload=1`);

  const refreshAll = () => {
    reload();
    sealSummary.reload();
    if (linked) one.reload();
  };

  const onDirectUploadAndSeal = async (fileList: FileList | null) => {
    const files = [...(fileList ?? [])];
    if (!files.length) return;
    setUploadingQes(true);
    setQesBannerMsg(null);
    try {
      const uploaded = await api.upload<any[]>(`/api/t/${tid}/documents`, files, { seal: '1' });
      refreshAll();
      const first = uploaded[0];
      const sealedCount = uploaded.filter((u) => Boolean(u.seal)).length;
      const convertedCount = uploaded.filter((u) => u.seal?.convertedToPdf).length;
      if (sealedCount === uploaded.length) {
        setQesBannerMsg({
          kind: 'pos',
          text: `${uploaded.length} ${uploaded.length === 1 ? 'Beleg' : 'Belege'} hochgeladen${convertedCount > 0 ? `, in Archiv-PDF konvertiert (${convertedCount})` : ''} und elektronisch versiegelt.`,
        });
      } else {
        setQesBannerMsg({
          kind: 'neg',
          text: `${uploaded.length} ${uploaded.length === 1 ? 'Beleg' : 'Belege'} hochgeladen, aber nur ${sealedCount}/${uploaded.length} konnten direkt versiegelt werden.`,
        });
      }
      if (first?.id) setLinked(first.id);
    } catch (e: any) {
      setQesBannerMsg({ kind: 'neg', text: e.message ?? 'Upload & Versiegelung fehlgeschlagen' });
    }
    setUploadingQes(false);
  };

  const onSealAllUnsealed = async () => {
    setSealingAll(true);
    setQesBannerMsg(null);
    try {
      const res = await api.post<{ processedCount: number; newlySealedCount: number; convertedToPdfCount: number }>(`/api/t/${tid}/documents/seals/seal-all`, {});
      refreshAll();
      setQesBannerMsg({
        kind: 'pos',
        text: `${res.newlySealedCount} ${res.newlySealedCount === 1 ? 'Beleg' : 'Belege'} versiegelt${res.convertedToPdfCount > 0 ? ` (${res.convertedToPdfCount} JPG/PNG/TXT nach PDF konvertiert)` : ''}.`,
      });
    } catch (e: any) {
      setQesBannerMsg({ kind: 'neg', text: e.message ?? 'Stapelversiegelung fehlgeschlagen' });
    }
    setSealingAll(false);
  };

  const onVerifyAllSeals = async () => {
    setVerifyingAll(true);
    setQesBannerMsg(null);
    try {
      const res = await api.post<{ totalSealed: number; validCount: number; invalidCount: number; allValid: boolean }>(`/api/t/${tid}/documents/seals/verify-all`, {});
      setQesBannerMsg({
        kind: res.allValid ? 'pos' : 'neg',
        text: res.allValid
          ? `Alle ${res.validCount} versiegelten Belege kryptografisch intakt (CMS PKCS#7 Signatur + X.509 Zertifikatskette + RFC 3161 Zeitstempel + SHA-256 Blob-Integrität geprüft).`
          : `Warnung: ${res.invalidCount} von ${res.totalSealed} Siegeln meldeten eine Abweichung.`,
      });
    } catch (e: any) {
      setQesBannerMsg({ kind: 'neg', text: e.message ?? 'Siegelprüfung fehlgeschlagen' });
    }
    setVerifyingAll(false);
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping() || modalOpen('.popover') || showMissingDeclModal || inspectSealDoc) return;
      if (e.key === '/') { e.preventDefault(); input.current?.focus(); input.current?.select(); }
      else if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter' && items[sel] && !(e.target as HTMLElement).closest?.('a, button, [role="button"]')) { e.preventDefault(); setOpen(items[sel]); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); if (q) { setQ(''); setDebounced(''); } else e.currentTarget.blur(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter') { e.preventDefault(); if (q.trim() !== debounced) setDebounced(q.trim()); else if (items[sel]) setOpen(items[sel]); }
  };

  const filtered = !!(kind || status || vendor.trim() || minAmount || maxAmount || sort);
  const exportHref = `/api/t/${tid}/documents/export.csv${params ? `?${params}` : ''}`;
  const sSum = sealSummary.data;

  return (
    <>
      <div className="page-head">
        <div><h2>Belege &amp; versiegelte Belege</h2><p>Jeder empfangene Beleg wird revisionssicher abgelegt, auf Wunsch als PDF konvertiert und elektronisch versiegelt (Integritätssiegel; qualifiziert nur mit einem qualifizierten Vertrauensdienst).</p></div>
        <div className="actions">
          <input ref={qesFileInput} type="file" accept="image/jpeg,image/png,image/webp,application/pdf,text/plain" multiple hidden onChange={(e) => { void onDirectUploadAndSeal(e.target.files); e.target.value = ''; }} />
          <a className="btn" href={exportHref} download title={params ? 'Exportiert die aktuelle Auswahl als CSV (Excel, Semikolon)' : 'Exportiert alle Belege als CSV (Excel, Semikolon)'}><I icon={Download} sm /> Export CSV</a>
          <RevisionExportButton tid={tid} />
          <button className="btn" onClick={() => setShowMissingDeclModal(true)} title="Erstellt und versiegelt eine Fehlender-Beleg-Deklaration (Ersatzbeleg)"><I icon={FilePlus2} sm /> Fehlender-Beleg-Deklaration</button>
          <button className="btn primary" disabled={uploadingQes} onClick={() => qesFileInput.current?.click()} title="Lädt JPG, PNG oder PDF hoch, konvertiert Bilder automatisch in PDF und versiegelt das Dokument">{uploadingQes ? <Spinner /> : <I icon={ShieldCheck} sm />} Beleg hochladen &amp; versiegeln</button>
        </div>
      </div>

      <section className="qes-strip" aria-label="Elektronisches Siegel">
        <div className="qes-strip-head">
          <div className="qes-strip-title">
            <h3><I icon={ShieldCheck} sm /> Elektronisches Siegel (CMS + RFC 3161)</h3>
            <Badge kind="pos">Integrität nachweisbar</Badge>
            <Badge>CMS PKCS#7 · RFC 3161 TSA</Badge>
          </div>
          <div className="qes-strip-actions">
            {sSum && sSum.unsealedCount > 0 && (
              <button className="btn sm" disabled={sealingAll} onClick={onSealAllUnsealed}>
                {sealingAll ? <Spinner /> : <I icon={Lock} sm />} Offene Belege versiegeln ({sSum.unsealedCount})
              </button>
            )}
            <button className="btn sm" disabled={verifyingAll || !sSum?.totalSealed} onClick={onVerifyAllSeals}>
              {verifyingAll ? <Spinner /> : <I icon={FileCheck2} sm />} Alle Siegel prüfen ({sSum?.totalSealed ?? 0})
            </button>
            <button className="btn sm" onClick={() => setShowQesExplainer((v) => !v)} aria-expanded={showQesExplainer}>
              <I icon={Info} sm /> {showQesExplainer ? 'Rechtsgrundlagen ausblenden' : 'Was bedeutet ein versiegelter Beleg?'}
            </button>
          </div>
        </div>

        <div className="qes-kpi-grid">
          <div className="qes-kpi-card">
            <span className="qes-kpi-label">Versiegelte Belege</span>
            <span className="qes-kpi-num">{sSum ? `${sSum.totalSealed} / ${sSum.totalDocuments}` : '—'}</span>
            <span className="qes-kpi-sub">CMS-Signatur und RFC-3161-Zeitstempel</span>
          </div>
          <div className="qes-kpi-card">
            <span className="qes-kpi-label">Digitale Archivierung (JPG/PNG → PDF)</span>
            <span className="qes-kpi-num">{sSum ? sSum.convertedFromImageCount : '—'}</span>
            <span className="qes-kpi-sub">Bilder automatisch in PDF konvertiert &amp; versiegelt</span>
          </div>
          <div className="qes-kpi-card">
            <span className="qes-kpi-label">Fehlender-Beleg-Deklarationen</span>
            <span className="qes-kpi-num">{sSum ? sSum.missingReceiptDeclarationCount : '—'}</span>
            <span className="qes-kpi-sub">Unabänderliche Ersatzbelege (OR Art. 957a / GeBüV)</span>
          </div>
          <div className="qes-kpi-card">
            <span className="qes-kpi-label">Aufbewahrung</span>
            <span className="qes-kpi-num">GeBüV Art. 3 &amp; 9</span>
            <span className="qes-kpi-sub">MWSTG Art. 70 · ElDI-V Art. 2–4 · OR Art. 958f</span>
          </div>
        </div>

        {qesBannerMsg && (
          <div className="split" style={{ padding: '0.55rem 0.75rem', borderRadius: 'var(--r-card)', border: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 'var(--fs-sm)' }}>
            <Badge kind={qesBannerMsg.kind}>{qesBannerMsg.kind === 'pos' ? 'Erledigt' : 'Hinweis'}</Badge>
            <span style={{ flex: 1 }}>{qesBannerMsg.text}</span>
            <button className="btn ghost sm" onClick={() => setQesBannerMsg(null)} aria-label="Schliessen"><I icon={X} sm /></button>
          </div>
        )}

        {showQesExplainer && (
          <div className="qes-pillars">
            <div className="qes-pillar">
              <strong><I icon={FileCheck2} sm /> 01 · Digitale Archivierung</strong>
              Beim Hochladen einer Quittung (JPG, PNG, WebP oder PDF) konvertiert Kontor Bilder automatisch in ein archivierbares A4-PDF, hängt das Siegel-Prüfzertifikat an und versiegelt das Dokument.
            </div>
            <div className="qes-pillar">
              <strong><I icon={ShieldCheck} sm /> 02 · Integritätsnachweis</strong>
              Das Siegel weist nach, dass Beleg und Archiv-PDF seit der Versiegelung unverändert sind (OR Art. 958f Abs. 3, GeBüV Art. 3 &amp; 9). Die gesetzliche Echtheitsvermutung eines qualifizierten Siegels (ZertES / eIDAS) gilt nur, wenn ein qualifizierter Vertrauensdienst konfiguriert ist.
            </div>
            <div className="qes-pillar">
              <strong><I icon={BookOpen} sm /> 03 · Steuer- &amp; Revisionsbereit</strong>
              Elektronisch aufbewahrte Belege sind für MWST und Revision zulässig, wenn Integrität und Lesbarkeit gewährleistet sind (MWSTG Art. 70 &amp; 81 Abs. 3, ElDI-V); das Siegel liefert dafür den Nachweis.
            </div>
            <div className="qes-pillar">
              <strong><I icon={Lock} sm /> 04 · Manipulationssicher (Tamper-Proof)</strong>
              CMS/PKCS#7-Signatur (`.p7s`), RFC-3161-Zeitstempel (`.tst`) und Append-Only-Datenbanktrigger stellen sicher, dass Herkunft und Inhalt weder bei Belegen noch bei Fehlender-Beleg-Deklarationen nachträglich verändert werden können.
            </div>
            <div className="qes-pillar">
              <strong><I icon={Info} sm /> 05 · Visueller Siegel-Indikator</strong>
              Sobald ein Beleg oder eine Fehlender-Beleg-Deklaration versiegelt ist, erscheint in der Belegliste neben dem Beleg das Siegel mit Info-Symbol zur direkten kryptografischen Live-Prüfung.
            </div>
          </div>
        )}
      </section>

      <div className="doc-toolbar" data-focus-section>
        <label className="input doc-search">
          <I icon={Search} sm className="dim" />
          <input ref={input} type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} placeholder="Lieferant, Nummer, Betrag, IBAN, Wort im Beleg…" aria-label="Belege durchsuchen" aria-controls="doc-results" autoComplete="off" spellCheck={false} />
          {loading && data ? <Spinner /> : <kbd className="only-desktop" aria-hidden>/</kbd>}
        </label>
        <div className="doc-filter"><Combobox value={kind} options={KIND_OPTS} onChange={setKind} ariaLabel="Nach Art filtern" searchPlaceholder="Art suchen…" width="100%" /></div>
        <div className="doc-filter"><Combobox value={status} options={STATUS_OPTS} onChange={setStatus} ariaLabel="Nach Status filtern" searchPlaceholder="Status suchen…" width="100%" /></div>
        <div className="doc-filter"><Combobox value={sort} options={SORT_OPTS} onChange={setSort} ariaLabel="Sortierung wählen" searchPlaceholder="Sortierung…" width="100%" /></div>
      </div>
      <div className="doc-toolbar">
        <label className="input doc-field">
          <input type="text" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Lieferant filtern…" aria-label="Lieferant filtern" />
        </label>
        <label className="input doc-field amount">
          <input type="number" inputMode="decimal" step="0.05" value={minChf} onChange={(e) => setMinChf(e.target.value)} placeholder="Min. CHF" aria-label="Mindestbetrag CHF" />
        </label>
        <label className="input doc-field amount">
          <input type="number" inputMode="decimal" step="0.05" value={maxChf} onChange={(e) => setMaxChf(e.target.value)} placeholder="Max. CHF" aria-label="Höchstbetrag CHF" />
        </label>
        {filtered && <button className="btn" onClick={reset}><I icon={FilterX} sm /> Filter zurücksetzen</button>}
      </div>

      {error ? <ErrorCard message={error} onRetry={reload} /> : (
        <div className="card" id="doc-results">
          {loading && !data ? <LoadingRows rows={8} /> : !items.length ? (
            debounced ? (
              <Empty icon={SearchX} title={`Keine Treffer für «${debounced}»`} action={<button className="btn" onClick={reset}>Suche zurücksetzen</button>}>
                Gesucht wird in Dateiname, Lieferant, Referenz, Zusammenfassung und Belegtext{filtered ? ', mit den gewählten Filtern' : ''}. Wortanfänge genügen, Umlaute sind egal.
              </Empty>
            ) : filtered ? (
              <Empty icon={FilterX} title="Keine Belege mit diesen Filtern" action={<button className="btn" onClick={reset}>Filter zurücksetzen</button>} />
            ) : (
              <Empty icon={Inbox} title="Noch keine Belege" action={<button className="btn primary" onClick={upload}><I icon={Upload} sm /> Belege hochladen</button>}>
                Belege kommen per Upload, per E-Mail an die Belegadresse oder im Chat an. Kontor legt jeden revisionssicher ab und macht ihn hier durchsuchbar.
              </Empty>
            )
          ) : (
            <>
              <div className="card-head doc-meta">
                <h3>
                  {data!.total} {debounced ? 'Treffer' : data!.total === 1 ? 'Beleg' : 'Belege'}
                  {(data!.total > items.length || offset > 0) && <span className="dim"> · {offset + 1}–{offset + items.length} angezeigt</span>}
                </h3>
                <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                  {offset > 0 && <button className="btn sm" onClick={() => setOffset(Math.max(0, offset - 200))}>← Vorherige 200</button>}
                  {offset + items.length < data!.total && <button className="btn sm" onClick={() => setOffset(offset + 200)}>Nächste 200 →</button>}
                  <span className="keys"><span><kbd>/</kbd> Suchen</span><span><kbd>j</kbd> <kbd>k</kbd> Auswahl</span><span><kbd>Enter</kbd> Öffnen</span></span>
                </div>
              </div>
              <div className="table-wrap">
                <table className="data docs">
                  <thead><tr><th>Eingang</th><th>Beleg &amp; Siegel</th><th>Art</th><th>Lieferant</th><th>Belegdatum</th><th className="r">Betrag CHF</th><th>Status</th><th>Buchung</th></tr></thead>
                  <tbody ref={body}>
                    {items.map((d, i) => {
                      const chfAmount = d.totalChf ?? (d.currency === 'CHF' ? d.total : null);
                      return (
                        <tr key={d.id} className="clickable" aria-selected={i === sel} onClick={(ev) => { if (!(ev.target as HTMLElement).closest('a,button') && !getSelection()?.toString()) { setSel(i); setOpen(d); } }}>
                          <td className="num">{localDate(d.created_at)}<span className="sub">{localTime(d.created_at)}</span></td>
                          <td className="doc-cell">
                            <span className="doc-name" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                              <I icon={FileText} sm className="dim" />
                              <span>{d.filename}</span>
                              {d.seal && (
                                <button
                                  type="button"
                                  className="qes-badge-btn"
                                  onClick={(ev) => { ev.stopPropagation(); setInspectSealDoc(d); }}
                                  title="Elektronisches Siegel · kryptografisches Prüfzertifikat anzeigen"
                                >
                                  <I icon={ShieldCheck} sm />
                                  <span>Siegel{d.seal.convertedToPdf ? ' · PDF' : ''}{d.seal.sealKind === 'missing_receipt_declaration' ? ' · Deklaration' : ''}</span>
                                  <I icon={Info} sm className="qes-info-trigger" />
                                </button>
                              )}
                            </span>
                            {d.snippet ? <span className="sub snip"><Snippet text={d.snippet} /></span> : d.summary && <span className="sub snip">{d.summary}</span>}
                          </td>
                          <td>{d.kind ? DOC_KIND[d.kind] ?? d.kind : <span className="dim">—</span>}</td>
                          <td>{d.vendor ?? <span className="dim">—</span>}{d.documentNumber && <span className="sub">Nr. {d.documentNumber}</span>}</td>
                          <td className="num">{date(d.docDate)}</td>
                          <td className="r num">{amt(chfAmount, false)}{d.currency && d.currency !== 'CHF' && d.total != null && <span className="sub">{money(d.total, d.currency)}</span>}</td>
                          <td><StatusPill status={d.status} /></td>
                          <td>{d.entry ? <a className="entry" href={`/t/${tid}/journal?e=${d.entry.id}`} onClick={(e) => { e.preventDefault(); navigate(`/t/${tid}/journal?e=${d.entry!.id}`); }} title={d.entry.reversed ? 'Storniert' : `Buchung Nr. ${d.entry.seq}`} style={d.entry.reversed ? { textDecoration: 'line-through' } : undefined}>#{d.entry.seq}</a>
                            : d.proposal?.status === 'pending' ? <a className="entry" href={`/t/${tid}/review?p=${d.proposal.id}`} onClick={(e) => { e.preventDefault(); navigate(`/t/${tid}/review?p=${d.proposal!.id}`); }}>Prüfen</a>
                              : <span className="dim">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="copy-hint card-pad" style={{ paddingTop: 'var(--sp-3)', paddingBottom: 'var(--sp-3)' }}>Klick auf eine Zeile öffnet den Beleg mit Vorschau, Siegel-Prüfung, erkannten Angaben und Prüfspur. Klick auf das Siegel-Info-Symbol neben einem Beleg öffnet direkt das Prüfzertifikat.</p>
            </>
          )}
        </div>
      )}

      {open && <DocumentDrawer tid={tid} hit={open} onClose={close} onSealed={refreshAll} />}
      {showMissingDeclModal && (
        <MissingReceiptDeclarationModal
          tid={tid}
          onClose={() => setShowMissingDeclModal(false)}
          onCreated={(docId, msg) => {
            setShowMissingDeclModal(false);
            setQesBannerMsg({ kind: 'pos', text: msg });
            refreshAll();
            setLinked(docId);
          }}
        />
      )}
      {inspectSealDoc && inspectSealDoc.seal && (
        <SealProbativeValuePopover
          tid={tid}
          hit={inspectSealDoc}
          seal={inspectSealDoc.seal}
          onClose={() => setInspectSealDoc(null)}
          onOpenDocument={() => {
            const d = inspectSealDoc;
            setInspectSealDoc(null);
            setOpen(d);
          }}
        />
      )}
    </>
  );
}

function KV({ rows }: { rows: [string, ReactNode][] }) {
  const r = rows.filter(([, v]) => v != null && v !== '' && v !== false);
  if (!r.length) return null;
  return (
    <table className="data compact kv" data-copy-surface>
      <tbody>{r.map(([k, v]) => <tr key={k}><td className="k" data-no-copy>{k}</td><td>{v}</td></tr>)}</tbody>
    </table>
  );
}

function Section({ icon, title, children }: { icon: React.ComponentType<any>; title: string; children: ReactNode }) {
  return <section><h4 className="doc-h"><I icon={icon} sm /> {title}</h4>{children}</section>;
}

/** Spesen (BC-06): the receipt was paid privately by an employee. The claim goes to review; the approval is the Spesenfreigabe. */
function ExpenseClaim({ tid, documentId }: { tid: string; documentId: string }) {
  const staff = useApi<any[]>(`/api/t/${tid}/employees`);
  const [employee, setEmployee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const opts: Option[] = (staff.data ?? []).map((e) => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }));
  if (!opts.length) return null;
  const claim = async () => {
    setBusy(true); setError(null);
    try { const p = await api.post<{ id: string }>(`/api/t/${tid}/documents/${documentId}/expense`, { employeeId: employee }); navigate(`/t/${tid}/review?p=${p.id}`); }
    catch (e: any) { setError(e.message); }
    setBusy(false);
  };
  return (
    <Section icon={Wallet} title="Spesen">
      <p className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Privat bezahlt? Der Betrag wird der Person geschuldet und über den Zahlungslauf zurückerstattet. Eine berechtigte Person gibt die Spesen frei.</p>
      <div className="split">
        <Combobox value={employee} options={opts} onChange={setEmployee} placeholder="Mitarbeiter wählen…" searchPlaceholder="Name…" width="16rem" ariaLabel="Mitarbeiter" />
        <button className="btn sm" disabled={!employee || busy} onClick={claim}>{busy ? <Spinner /> : <I icon={Wallet} sm />} Als Spesen erfassen</button>
      </div>
      {error && <ErrorCard message={error} />}
    </Section>
  );
}

function DocumentDrawer({ tid, hit, onClose, onSealed }: { tid: string; hit: DocHit; onClose: () => void; onSealed?: () => void }) {
  const { data: doc, error, loading, reload } = useApi<any>(`/api/t/${tid}/documents/${hit.id}`);
  const sealRes = useApi<{ seal: any; verification: any }>(`/api/t/${tid}/documents/${hit.id}/seal`);
  const entry = useApi<any>(hit.entry ? `/api/t/${tid}/journal/${hit.entry.id}` : null);
  const names = useAccounts(tid);
  const [sealingSingle, setSealingSingle] = useState(false);
  const [sealErr, setSealErr] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'sealed' | 'original'>('sealed');
  const file = `/api/t/${tid}/documents/${hit.id}/file`;
  const sealedPdfUrl = `/api/t/${tid}/documents/${hit.id}/sealed.pdf`;
  const p7sUrl = `/api/t/${tid}/documents/${hit.id}/seal.p7s`;
  const tstUrl = `/api/t/${tid}/documents/${hit.id}/seal.tst`;
  const manifestUrl = `/api/t/${tid}/documents/${hit.id}/seal-manifest.txt`;
  const activeSeal = sealRes.data?.seal ?? doc?.seal ?? hit.seal ?? null;
  const activeVerification = sealRes.data?.verification ?? null;
  const x = doc?.extraction ?? null;
  const items: any[] | null = x && [x.lines, x.lineItems, x.items].find((a) => Array.isArray(a) && a.length) || null;
  const fields = Object.entries(hit.fields ?? {});
  const go = (to: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(to); };

  const sealNow = async () => {
    setSealingSingle(true);
    setSealErr(null);
    try {
      await api.post(`/api/t/${tid}/documents/${hit.id}/seal`, {});
      reload();
      sealRes.reload();
      onSealed?.();
    } catch (e: any) {
      setSealErr(e.message ?? 'Versiegelung fehlgeschlagen');
    }
    setSealingSingle(false);
  };

  return (
    <Drawer title={hit.filename} subtitle={`${hit.kind ? DOC_KIND[hit.kind] ?? hit.kind : 'Noch nicht gelesen'} · ${CHANNEL[hit.channel] ?? hit.channel} · ${dateTime(hit.created_at)}`} onClose={onClose}
      actions={
        <>
          <PromptButton tid={tid} documentId={hit.id} />
          {activeSeal && <a className="btn sm" href={sealedPdfUrl} target="_blank" rel="noreferrer"><I icon={ShieldCheck} sm /> Versiegeltes PDF <I icon={ExternalLink} sm /></a>}
          <a className="btn sm" href={file} target="_blank" rel="noreferrer">Original <I icon={ExternalLink} sm /></a>
        </>
      }>
      <div className="split" style={{ flexWrap: 'wrap' }}>
        <StatusPill status={hit.status} />
        {activeSeal ? (
          <Badge kind="pos"><I icon={ShieldCheck} sm /> Versiegelt · {activeSeal.convertedToPdf ? 'JPG/PNG → PDF' : 'Archiv-PDF'}</Badge>
        ) : (
          <button className="btn sm primary" disabled={sealingSingle} onClick={sealNow}>
            {sealingSingle ? <Spinner /> : <I icon={ShieldCheck} sm />} Jetzt als PDF archivieren &amp; versiegeln
          </button>
        )}
        {hit.entry && <a className="btn sm" href={`/t/${tid}/journal?e=${hit.entry.id}`} onClick={go(`/t/${tid}/journal?e=${hit.entry.id}`)}><I icon={BookOpen} sm /> Buchung Nr. {hit.entry.seq}{hit.entry.reversed ? ' (storniert)' : ''}</a>}
        {hit.proposal && (hit.proposal.status === 'pending'
          ? <a className="btn sm" href={`/t/${tid}/review?p=${hit.proposal.id}`} onClick={go(`/t/${tid}/review?p=${hit.proposal.id}`)}><I icon={ListChecks} sm /> Vorschlag prüfen</a>
          : !hit.entry && <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{PROPOSAL[hit.proposal.status] ?? hit.proposal.status}</span>)}
      </div>

      {sealErr && <ErrorCard message={sealErr} />}

      {activeSeal && activeSeal.convertedToPdf && (
        <div className="split" style={{ gap: 'var(--sp-2)' }}>
          <button type="button" className={`btn sm ${previewMode === 'sealed' ? 'primary' : ''}`} onClick={() => setPreviewMode('sealed')}>
            <I icon={ShieldCheck} sm /> Versiegeltes Archiv-PDF
          </button>
          <button type="button" className={`btn sm ${previewMode === 'original' ? 'primary' : ''}`} onClick={() => setPreviewMode('original')}>
            <I icon={FileText} sm /> Ursprüngliche Datei ({activeSeal.originalMime})
          </button>
        </div>
      )}

      <div className="doc-preview">
        {activeSeal && previewMode === 'sealed' ? <iframe title={`Versiegeltes PDF ${hit.filename}`} src={sealedPdfUrl} />
          : hit.mime === 'application/pdf' ? <iframe title={`Vorschau ${hit.filename}`} src={file} />
          : hit.mime.startsWith('image/') ? <img alt={hit.filename} src={file} />
            : error ? <div className="card-pad"><ErrorCard message={error} onRetry={reload} /></div>
              : loading && !doc ? <div className="card-pad stack-sm"><Skeleton h={14} /><Skeleton h={14} w="80%" /><Skeleton h={14} w="60%" /></div>
                : <pre className="mono">{doc?.text ?? 'Kein Textinhalt gespeichert.'}</pre>}
      </div>

      <Section icon={ShieldCheck} title="Elektronisches Siegel">
        {activeSeal ? (
          <div className="stack-sm">
            <div className="split" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
              <Badge kind={activeVerification?.valid === false ? 'neg' : 'pos'}>
                {activeVerification?.valid === false ? 'Integritätsabweichung erkannt' : 'Kryptografisch verifiziert'}
              </Badge>
              <Badge>{activeSeal.sealKind === 'missing_receipt_declaration' ? 'Fehlender-Beleg-Deklaration (Ersatzbeleg)' : 'Versiegelter Originalbeleg'}</Badge>
              {activeSeal.convertedToPdf && <Badge>Konvertiert: {activeSeal.originalMime} → application/pdf</Badge>}
            </div>
            <p className="dim" style={{ fontSize: 'var(--fs-xs)', margin: 0 }}>{activeSeal.probativeValueDe}</p>
            <KV rows={[
              ['Siegel-ID', <span className="mono">{activeSeal.id}</span>],
              ['Standard', activeSeal.sealStandard],
              ['Vertrauensdienst (TSP)', activeSeal.sealProvider],
              ['Siegel-Zertifikat', <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{activeSeal.certSubject}</span>],
              ['Zertifikat SHA-256', <span className="mono hash">{activeSeal.certFingerprintSha256}</span>],
              ['Original-Datei', `${activeSeal.originalMime} · SHA-256 ${activeSeal.originalSha256.slice(0, 24)}…`],
              ['Versiegeltes PDF', `${activeSeal.sealedPdfFilename} (${(activeSeal.sealedPdfSize / 1024).toFixed(1)} KB)`],
              ['PDF SHA-256', <span className="mono hash">{activeSeal.sealedPdfSha256}</span>],
              ['RFC 3161 Zeitstempel', `${dateTime(activeSeal.tsaStampedAt)} · Policy ${activeSeal.tsaPolicyOid} · Serial ${activeSeal.tsaSerialHex}`],
              ['Rechtsgrundlagen', Array.isArray(activeSeal.probativeLawRefs) ? activeSeal.probativeLawRefs.join(' · ') : 'GeBüV Art. 3 & 9 · OR Art. 958f · MWSTG Art. 70'],
              ['Versiegelt durch', `${activeSeal.sealedBy} am ${dateTime(activeSeal.sealedAt)}`],
            ]} />
            {activeSeal.declaration && (
              <KV rows={[
                ['Deklaration · Lieferant', activeSeal.declaration.vendor],
                ['Deklaration · Geschäftszweck', activeSeal.declaration.businessPurpose],
                ['Grund für fehlenden Beleg', activeSeal.declaration.missingReason],
                ['Erklärende Person', `${activeSeal.declaration.declarantName} (${activeSeal.declaration.declarantRole})`],
              ]} />
            )}
            {activeVerification?.checks && (
              <div className="qes-checks-grid">
                {activeVerification.checks.map((chk: any) => (
                  <div key={chk.id} className="qes-check-item" data-ok={String(chk.ok)}>
                    <I icon={chk.ok ? Check : TriangleAlert} sm />
                    <div>
                      <div style={{ fontWeight: 600 }}>{chk.label}</div>
                      <div className="dim" style={{ wordBreak: 'break-all' }}>{chk.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="split" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <a className="btn sm" href={sealedPdfUrl} download><I icon={Download} sm /> Versiegeltes PDF (.pdf)</a>
              <a className="btn sm" href={p7sUrl} download><I icon={Lock} sm /> CMS-Signatur (.p7s)</a>
              <a className="btn sm" href={tstUrl} download><I icon={FileCheck2} sm /> Zeitstempel (.tst)</a>
              <a className="btn sm" href={manifestUrl} download><I icon={FileText} sm /> Manifest (.txt)</a>
              <button type="button" className="btn sm" onClick={() => sealRes.reload()}><I icon={ShieldCheck} sm /> Siegel erneut prüfen</button>
            </div>
          </div>
        ) : (
          <div className="stack-sm">
            <p className="dim" style={{ fontSize: 'var(--fs-sm)', margin: 0 }}>
              Dieser Beleg ist noch nicht versiegelt. Mit einem Klick konvertiert Kontor Bilder/Text in ein archivierbares A4-PDF, bringt die CMS/PKCS#7-Signatur sowie den RFC-3161-Zeitstempel an und weist so die Integrität nach (GeBüV Art. 3 &amp; 9, MWSTG Art. 70).
            </p>
            <div>
              <button className="btn sm primary" disabled={sealingSingle} onClick={sealNow}>
                {sealingSingle ? <Spinner /> : <I icon={ShieldCheck} sm />} Jetzt versiegeln
              </button>
            </div>
          </div>
        )}
      </Section>

      {x && (
        <Section icon={ScanText} title="Erkannte Angaben">
          <KV rows={[
            ['Lieferant', x.vendor], ['UID', x.vendorUid], ['Belegnummer', x.documentNumber], ['Belegdatum', x.date && date(x.date)], ['Fällig', x.dueDate && date(x.dueDate)],
            ['Total', x.total != null && <span className="num">{money(x.total, x.currency)}</span>],
            ['Total CHF', x.currency && x.currency !== 'CHF' && (x.totalChf != null
              ? <span className="num">{money(x.totalChf)}{x.fx?.label ? <span className="dim"> · {x.fx.label}</span> : x.fxRate ? <span className="dim"> · Kurs {x.fxRate}</span> : null}</span>
              : x.fx?.status === 'missing' && <span className="neg">offen · {x.fx.label}</span>)],
            ['Kursquelle', x.fx?.rate && <a href={x.fx.rate.sourceUrl} target="_blank" rel="noreferrer">BAZG, {x.fx.rate.method === 'monthly' ? `Monatsmittelkurse ${x.fx.rate.published}` : `Tageskurse vom ${date(x.fx.rate.published)}`}</a>],
            ['MWST total', hit.vatTotal != null && hit.vatTotal > 0 && <span className="num">{money(hit.vatTotal, x.currency)}</span>],
            ['Zahlung', x.paymentMethod && `${PAY[x.paymentMethod] ?? x.paymentMethod}${x.paid ? ', bezahlt' : ''}`], ['IBAN', x.iban && <span className="mono">{iban(x.iban)}</span>],
            ['Referenz', x.reference && <span className="mono">{x.reference}</span>], ['Beschreibung', x.description],
            ['Kontierung', x.suggestedAccount && `${x.suggestedAccount} ${names[x.suggestedAccount] ?? ''}${x.suggestedVatCode ? ` · ${x.suggestedVatCode}` : ''}`],
            ['Erkennung', <span className="split" style={{ gap: 'var(--sp-2)' }}>{typeof x.confidence === 'number' && <Confidence value={x.confidence} />}<span className="dim">{x.engine === 'claude' ? 'Claude' : 'Offline-Heuristik'}</span></span>],
          ]} />
          {x.warnings?.length > 0 && <div className="stack-sm" style={{ marginTop: 'var(--sp-3)' }}>{x.warnings.map((w: string, i: number) => <div key={i} className="doc-warn"><I icon={TriangleAlert} sm /> {w}</div>)}</div>}
        </Section>
      )}

      {items && (
        <Section icon={FileText} title="Positionen">
          <LineItems items={items} currency={x.currency} />
        </Section>
      )}

      {x?.vatLines?.length > 0 && (
        <Section icon={FileText} title="MWST">
          <table className="data compact" data-copy-surface>
            <thead><tr><th>Satz</th><th className="r">Brutto</th><th className="r">MWST</th></tr></thead>
            <tbody>{x.vatLines.map((v: any, i: number) => <tr key={i}><td className="num">{v.rate / 100} %</td><td className="r num">{v.gross ? amt(v.gross, false) : '—'}</td><td className="r num">{amt(v.amount, false)}</td></tr>)}</tbody>
          </table>
        </Section>
      )}

      {x?.qr && (
        <Section icon={QrCode} title="QR-Rechnung">
          <KV rows={[
            ['Empfänger', [x.qr.creditor?.name, [x.qr.creditor?.zip, x.qr.creditor?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')],
            [x.qr.isQrIban ? 'QR-IBAN' : 'IBAN', x.qr.iban && <span className="mono">{iban(x.qr.iban)}</span>],
            ['Betrag', x.qr.amount != null ? money(x.qr.amount, x.qr.currency) : 'offen'],
            ['Referenz', x.qr.reference && <span className="mono">{x.qr.referenceType} {x.qr.reference}</span>], ['Mitteilung', x.qr.message], ['Zahlungspflichtig', x.qr.debtor?.name],
            ['Prüfung', x.qr.valid ? <Badge kind="pos">gültig</Badge> : <Badge kind="neg">ungültig: {x.qr.errors?.join(', ')}</Badge>],
          ]} />
        </Section>
      )}

      {fields.length > 0 && (
        <Section icon={Tags} title="Zusatzfelder">
          <KV rows={fields.map(([k, v]) => [k, v == null || v === '' ? null : typeof v === 'object' ? JSON.stringify(v) : typeof v === 'boolean' ? (v ? 'Ja' : 'Nein') : String(v)])} />
        </Section>
      )}

      {hit.entry && (
        <Section icon={BookOpen} title={`Buchung Nr. ${hit.entry.seq} · ${date(hit.entry.date)}`}>
          {entry.error ? <ErrorCard message={entry.error} onRetry={entry.reload} /> : !entry.data ? <Skeleton h={48} /> : (
            <table className="data compact" data-copy-surface>
              <thead><tr><th>Konto</th><th>MWST</th><th className="r">Soll</th><th className="r">Haben</th></tr></thead>
              <tbody>{entry.data.lines.map((l: any, i: number) => (
                <tr key={i}><td><span className="mono">{l.account}</span> {names[l.account]}</td><td>{l.vatCode ? `${l.vatCode}${l.vatRole === 'tax' ? ' Steuer' : ''}` : '—'}</td><td className="r num">{amt(l.debit)}</td><td className="r num">{amt(l.credit)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </Section>
      )}

      {x && !hit.entry && (hit.kind === 'receipt' || hit.kind === 'invoice_in') && <ExpenseClaim tid={tid} documentId={hit.id} />}

      <Section icon={ShieldCheck} title="Ablage">
        <KV rows={[
          ['SHA-256', <span className="mono hash">{hit.sha256}</span>], ['Eingang', dateTime(hit.created_at)], ['Kanal', CHANNEL[hit.channel] ?? hit.channel],
          ['Datei', `${hit.mime} · ${hit.size < 1024 ? `${hit.size} B` : `${(hit.size / 1024).toFixed(1)} KB`}`], ['Beleg-ID', <span className="mono">{hit.id}</span>],
        ]} />
        <p className="dim" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-2)' }}>Der Hash steht in der Prüfspur. Jede nachträgliche Änderung am gespeicherten Beleg wäre erkennbar (GeBüV).</p>
      </Section>
    </Drawer>
  );
}

function SealProbativeValuePopover({
  tid,
  hit,
  seal,
  onClose,
  onOpenDocument,
}: {
  tid: string;
  hit: DocHit;
  seal: ReceiptSealBadge;
  onClose: () => void;
  onOpenDocument: () => void;
}) {
  const verify = useApi<any>(`/api/t/${tid}/documents/${hit.id}/seal/verify`);
  return (
    <div className="qes-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="QES Beweiskraft-Bescheinigung">
      <div className="qes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qes-strip-head">
          <div className="qes-strip-title">
            <h3><I icon={ShieldCheck} sm /> Versiegelter Beleg · {hit.filename}</h3>
            <Badge kind="pos">Integrität nachweisbar</Badge>
          </div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Schliessen"><I icon={X} sm /></button>
        </div>

        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--fg-2)' }}>{seal.probativeValueDe}</p>

        <div className="qes-pillars">
          <div className="qes-pillar">
            <strong><I icon={FileCheck2} sm /> Digitale Archivierung</strong>
            {seal.convertedToPdf
              ? `Bilddatei (${seal.originalMime}) automatisch in archivierbares A4-PDF konvertiert und versiegelt.`
              : `PDF-Dokument (${seal.originalMime}) mit angehängtem Siegel-Prüfzertifikat versiegelt.`}
          </div>
          <div className="qes-pillar">
            <strong><I icon={ShieldCheck} sm /> Rechtliche Wirkung</strong>
            {seal.probativeValueDe}
          </div>
          <div className="qes-pillar">
            <strong><I icon={BookOpen} sm /> Steuer- &amp; Revisionsbereit</strong>
            Anerkannt für MWST-Vorsteuerabzug, Steuerrevisionen und Jahresabschlussprüfung (MWSTG Art. 70 &amp; 81 Abs. 3, ElDI-V Art. 2–4).
          </div>
          <div className="qes-pillar">
            <strong><I icon={Lock} sm /> Manipulationssicher</strong>
            Herkunft und Inhalt sind durch CMS/PKCS#7-Signatur, RFC-3161-Zeitstempel und Append-Only-Speicherung nach Erstellung unveränderbar.
          </div>
        </div>

        <KV rows={[
          ['Siegel-ID', <span className="mono">{seal.id}</span>],
          ['Dokument-Typ', seal.sealKind === 'missing_receipt_declaration' ? 'Fehlender-Beleg-Deklaration (Ersatzbeleg)' : 'Versiegelter Originalbeleg'],
          ['Siegel-Zertifikat', <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{seal.certSubject}</span>],
          ['Zertifikat SHA-256', <span className="mono hash">{seal.certFingerprintSha256}</span>],
          ['Original SHA-256', <span className="mono hash">{seal.originalSha256}</span>],
          ['Versiegeltes PDF SHA-256', <span className="mono hash">{seal.sealedPdfSha256}</span>],
          ['RFC 3161 Zeitstempel', `${dateTime(seal.tsaStampedAt)} · Serial ${seal.tsaSerialHex}`],
          ['Live-Verifikation', verify.loading ? <Spinner /> : verify.data?.valid ? <Badge kind="pos">Alle 7 kryptografischen Prüfungen bestanden</Badge> : <Badge kind="neg">Prüfung fehlgeschlagen</Badge>],
        ]} />

        <div className="split" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
          <div className="split" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            <a className="btn sm" href={`/api/t/${tid}/documents/${hit.id}/sealed.pdf`} target="_blank" rel="noreferrer"><I icon={ExternalLink} sm /> Versiegeltes PDF öffnen</a>
            <a className="btn sm" href={`/api/t/${tid}/documents/${hit.id}/seal.p7s`} download><I icon={Lock} sm /> CMS (.p7s)</a>
            <a className="btn sm" href={`/api/t/${tid}/documents/${hit.id}/seal.tst`} download><I icon={FileCheck2} sm /> Zeitstempel (.tst)</a>
          </div>
          <div className="split" style={{ gap: 'var(--sp-2)' }}>
            <button className="btn sm primary" onClick={onOpenDocument}>Im Beleg-Drawer öffnen</button>
            <button className="btn sm" onClick={onClose}>Schliessen</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MissingReceiptDeclarationModal({
  tid,
  onClose,
  onCreated,
}: {
  tid: string;
  onClose: () => void;
  onCreated: (docId: string, msg: string) => void;
}) {
  const [vendor, setVendor] = useState('SBB CFF FFS · Billettautomat Zürich HB');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [amountChf, setAmountChf] = useState('46.80');
  const [currency, setCurrency] = useState('CHF');
  const [vatRatePct, setVatRatePct] = useState('8.1');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'cash' | 'bank' | 'twint'>('card');
  const [categoryAccount, setCategoryAccount] = useState('5820 Reisespesen');
  const [businessPurpose, setBusinessPurpose] = useState('Kundenbesuch & Mandatsbesprechung vor Ort in Bern');
  const [missingReason, setMissingReason] = useState('Thermopapier-Quittung am Billettautomaten nicht gedruckt; Kartentransaktion auf Firmenkarte belastet.');
  const [declarantName, setDeclarantName] = useState('Anna Keller');
  const [declarantRole, setDeclarantRole] = useState('Mandatsleiterin · Treuhandbüro');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const amountRappen = Math.round(Number(amountChf) * 100);
      const vatRateBps = vatRatePct.trim() !== '' ? Math.round(Number(vatRatePct) * 100) : null;
      const res = await api.post<{ document: { id: string; filename: string }; seal: ReceiptSealBadge }>(
        `/api/t/${tid}/documents/missing-receipt-declaration`,
        {
          vendor,
          date: expenseDate,
          amountRappen,
          currency,
          vatRateBps,
          paymentMethod,
          categoryAccount,
          businessPurpose,
          missingReason,
          declarantName,
          declarantRole,
        }
      );
      onCreated(
        res.document.id,
        `Fehlender-Beleg-Deklaration «${res.document.filename}» als PDF generiert und elektronisch versiegelt (SHA-256 ${res.seal.sealedPdfSha256.slice(0, 12)}…).`
      );
    } catch (ex: any) {
      setErr(ex.message ?? 'Erstellung fehlgeschlagen');
    }
    setBusy(false);
  };

  return (
    <div className="qes-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Fehlender-Beleg-Deklaration erstellen und mit QES versiegeln">
      <form className="qes-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="qes-strip-head">
          <div className="qes-strip-title">
            <h3><I icon={FilePlus2} sm /> Fehlender-Beleg-Deklaration erstellen &amp; versiegeln</h3>
            <Badge kind="pos">OR Art. 957a · GeBüV Art. 9</Badge>
          </div>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Schliessen"><I icon={X} sm /></button>
        </div>

        <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--fg-2)' }}>
          Wenn ein Originalbeleg verloren ging oder nicht ausgestellt wurde, erstellt Kontor einen revisionssicheren Ersatzbeleg als PDF/A und versiegelt ihn sofort mit CMS-Signatur und RFC-3161-Zeitstempel. Nach der Versiegelung können Herkunft und Inhalt nicht mehr verändert werden.
        </p>

        <div className="qes-form-grid">
          <label className="qes-field">
            <span>Lieferant / Leistungserbringer *</span>
            <input className="input" type="text" required value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </label>
          <label className="qes-field">
            <span>Transaktionsdatum *</span>
            <input className="input" type="date" required value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
          </label>
          <label className="qes-field">
            <span>Betrag (Brutto) *</span>
            <input className="input" type="number" step="0.01" min="0.05" required value={amountChf} onChange={(e) => setAmountChf(e.target.value)} />
          </label>
          <label className="qes-field">
            <span>Währung</span>
            <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option value="CHF">CHF</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </label>
          <label className="qes-field">
            <span>MWST-Satz (%)</span>
            <select className="input" value={vatRatePct} onChange={(e) => setVatRatePct(e.target.value)}>
              <option value="8.1">8.1 % (Normalsatz)</option>
              <option value="3.8">3.8 % (Beherbergung)</option>
              <option value="2.6">2.6 % (Reduzierter Satz)</option>
              <option value="0">0.0 % (MWST-frei / Ausland)</option>
            </select>
          </label>
          <label className="qes-field">
            <span>Zahlungsmittel</span>
            <select className="input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as any)}>
              <option value="card">Firmenkarte / Kreditkarte</option>
              <option value="twint">TWINT</option>
              <option value="cash">Barzahlung</option>
              <option value="bank">Bankbelastung</option>
            </select>
          </label>
          <label className="qes-field">
            <span>Aufwandkonto / Kategorie</span>
            <input className="input" type="text" value={categoryAccount} onChange={(e) => setCategoryAccount(e.target.value)} />
          </label>
          <label className="qes-field">
            <span>Erklärende Person &amp; Funktion</span>
            <input className="input" type="text" value={`${declarantName} · ${declarantRole}`} onChange={(e) => {
              const parts = e.target.value.split('·').map((s) => s.trim());
              setDeclarantName(parts[0] || e.target.value);
              if (parts[1]) setDeclarantRole(parts[1]);
            }} />
          </label>
        </div>

        <label className="qes-field">
          <span>Geschäftlicher Zweck der Ausgabe (min. 5 Zeichen) *</span>
          <input className="input" type="text" required value={businessPurpose} onChange={(e) => setBusinessPurpose(e.target.value)} />
        </label>

        <label className="qes-field">
          <span>Begründung für das Fehlen des Originalbelegs (min. 5 Zeichen) *</span>
          <textarea className="input" rows={2} required value={missingReason} onChange={(e) => setMissingReason(e.target.value)} style={{ padding: 'var(--sp-2)', resize: 'vertical' }} />
        </label>

        {err && <ErrorCard message={err} />}

        <div className="split" style={{ justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button type="button" className="btn" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? <Spinner /> : <I icon={ShieldCheck} sm />} Deklaration als PDF erstellen &amp; versiegeln
          </button>
        </div>
      </form>
    </div>
  );
}
